import React, { useState } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SmartImage from '../SmartImage';
import { Change, StatisticsRow } from '../../types/supplierStatistics';
export const number = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('sv-SE', { maximumFractionDigits: 1 });
export const money = (v: number | null | undefined): string =>
  v == null ? '—' : `${number(Math.round(v))} kr`;
export const percent = (v: number | null | undefined): string =>
  v == null ? '—' : `${number(v)} %`;
export function Delta({
  value,
  units = false,
}: {
  value: Change | undefined;
  units?: boolean;
}) {
  if (!value) return <>—</>;
  return (
    <Tooltip
      title={
        value.percent === null
          ? 'Procent kan inte beräknas när föregående värde är noll eller negativt. Ny leverantör eller saknat historiskt underlag kan ge samma utfall.'
          : 'Förändring av observerat underlag; full periodtäckning är inte verifierad.'
      }
    >
      <Box
        component="span"
        sx={{
          whiteSpace: 'nowrap',
          color:
            value.value > 0
              ? 'success.main'
              : value.value < 0
                ? 'error.main'
                : 'text.secondary',
        }}
      >
        {value.value > 0 ? '+' : ''}
        {units ? number(value.value) : money(value.value)}
        <Typography component="span" variant="caption">
          {' '}
          ({percent(value.percent)})
        </Typography>
      </Box>
    </Tooltip>
  );
}
export function StockStatus({ row }: { row: StatisticsRow }) {
  if (!row.stockKnown)
    return <Chip size="small" label="Lagerunderlag saknas" />;
  return (
    <Box>
      <Typography variant="caption">
        {row.daysOfStockLeft === null
          ? 'Ingen positiv försäljningstakt'
          : `≈ ${number(row.daysOfStockLeft)} dagars täckning`}
      </Typography>
      {row.stockAlert && (
        <Chip
          size="small"
          sx={{ ml: 1 }}
          color={row.stockAlert === 'reorder' ? 'error' : 'warning'}
          label={
            row.stockAlert === 'reorder' ? 'Påfyllnad ≤14 dagar' : 'Överlager'
          }
        />
      )}
    </Box>
  );
}
export default function StatisticsTable({
  rows,
  level,
  href,
  compact = false,
}: {
  rows: StatisticsRow[];
  level: 'supplier' | 'brand' | 'product';
  href: (row: StatisticsRow) => string;
  compact?: boolean;
}) {
  const [details, setDetails] = useState(false);
  return (
    <Box sx={{ minWidth: 0 }}>
      {!compact && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={details}
                onChange={(_, checked) => setDetails(checked)}
              />
            }
            label="Fler kolumner"
          />
        </Box>
      )}
      <TableContainer
        tabIndex={0}
        role="region"
        aria-label="Statistiktabell, rulla i sidled för fler kolumner"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Table
          size="small"
          aria-label="Försäljning och jämförelse med föregående år"
          sx={{
            '& th': {
              bgcolor: 'background.default',
              color: 'text.secondary',
              fontSize: 12,
            },
            '& td': { py: 1.5 },
            '& tr:last-child td': { borderBottom: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                {level === 'supplier'
                  ? 'Leverantör'
                  : level === 'brand'
                    ? 'Varumärke'
                    : 'Produkt / färg'}
              </TableCell>
              <TableCell align="right">Omsättning vald period</TableCell>
              <TableCell align="right">Butik²</TableCell>
              <TableCell align="right">Online inkl moms</TableCell>
              <TableCell align="right">Förändring kr / %</TableCell>
              {!compact && (
                <TableCell align="right">Antal¹ vald period</TableCell>
              )}
              {!compact && <TableCell align="right">Aktuellt lager</TableCell>}
              {details && (
                <>
                  <TableCell align="right">Omsättning föregående år</TableCell>
                  <TableCell align="right">Antal¹ föregående år</TableCell>
                  <TableCell align="right">Förändring antal / %</TableCell>
                  <TableCell align="right">Butik nettorader</TableCell>
                  <TableCell align="right">Online beställda enheter</TableCell>
                  {level === 'product' && (
                    <>
                      <TableCell align="right">
                        Kalkylmarginal ex moms
                      </TableCell>
                      <TableCell align="right">
                        Periodmarginal (uppskattning)
                      </TableCell>
                      <TableCell>Lagerbedömning</TableCell>
                    </>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow
                hover
                key={`${r.supplierId}-${r.brandName}-${r.productId}-${r.attribute1Code}-${i}`}
              >
                <TableCell sx={{ minWidth: level === 'product' ? 240 : 170 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {level === 'product' && (
                      <Box
                        sx={{
                          width: 56,
                          height: 64,
                          flexShrink: 0,
                          bgcolor: '#f8fafc',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <SmartImage
                          src={r.imageUrl}
                          alt={r.productName || r.productNumber}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )}
                    <Box>
                      <Link
                        component={RouterLink}
                        to={href(r)}
                        underline="hover"
                        sx={{ fontWeight: 600 }}
                      >
                        {level === 'supplier'
                          ? r.supplierName
                          : level === 'brand'
                            ? r.brandName
                            : r.productName || r.productNumber}
                      </Link>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {level === 'product'
                          ? `${r.productNumber} · ${r.colorName || r.attribute1Code || (r.isProductAggregate ? 'Alla färger' : 'Saknad färgkoppling')}${r.missingArticle ? ' · Artikelkoppling saknas' : ''}`
                          : `${level === 'supplier' ? `${number(r.brandCount)} varumärken · ` : ''}${number(r.productCount)} produkter`}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                >
                  {money(r.totalRevenue)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: 'nowrap', color: '#127d76' }}
                >
                  {money(r.retailRevenue)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: 'nowrap', color: 'primary.main' }}
                >
                  {money(r.onlineRevenue)}
                </TableCell>
                <TableCell align="right">
                  {r.hasComparisonObservations ? (
                    <Delta value={r.revenueChange} />
                  ) : (
                    'Ej jämförbart'
                  )}
                </TableCell>
                {!compact && (
                  <TableCell align="right">
                    {number(r.totalUnitsSold)}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell align="right">
                    {r.stockKnown ? number(r.availableStock) : '—'}
                  </TableCell>
                )}
                {details && (
                  <>
                    <TableCell align="right">
                      {r.hasComparisonObservations
                        ? money(r.previousRevenue)
                        : '— (ingen observerad historik)'}
                    </TableCell>
                    <TableCell align="right">
                      {r.hasComparisonObservations
                        ? number(r.previousUnitsSold)
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {r.hasComparisonObservations ? (
                        <Delta value={r.unitsChange} units />
                      ) : (
                        'Ej jämförbart'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {number(r.retailUnitsSold)}
                    </TableCell>
                    <TableCell align="right">
                      {number(r.onlineUnitsSold)}
                    </TableCell>
                    {level === 'product' && (
                      <>
                        <TableCell align="right">
                          {percent(r.calculationMarginPercent)}
                        </TableCell>
                        <TableCell align="right">
                          {percent(r.periodMarginEstimatePercent)}
                        </TableCell>
                        <TableCell>
                          <StockStatus row={r} />
                        </TableCell>
                      </>
                    )}
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
